import React from 'react';
import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiClient } from '../api/client';
import { apiDebug } from '../utils/debug';
import { extractErrorMessage } from '../utils/errorHandling';
import {
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  User,
  UserCreate,
  UserUpdate,
  UserRoleUpdate,
  Client,
  ClientCreate,
  ClientUpdate,
  // ClientListResponse,
  Candidate,
  CandidateCreate,
  CandidateUpdate,
  Submission,
  SubmissionCreate,
  SubmissionUpdate,
  Call,
  CallCreate,
  CallUpdate,
  CallTranscriptUpdate,
  CVAnalysis,
  JDAnalysis,
  FitmentScore,
  InterviewQuestions,
  Query,
  QueryCreate,
  QueryUpdate,
  DashboardKPIs,
  CandidatePipelineAnalytics,
  RecruiterPerformanceAnalytics,
  EmailTemplate,
  EmailSend,
  EmailTracking,
  ComplianceChecklist,
  PipelineStatus,
  AuditLog,
  SearchFilters,
  PaginatedResponse,
  PaginatedUsersResponse,
  ApiResponse,
} from '../types/api';

// Query Keys
export const queryKeys = {
  // Health
  health: ['health'] as const,
  healthReady: ['health', 'ready'] as const,
  healthDetailed: ['health', 'detailed'] as const,
  healthMetrics: ['health', 'metrics'] as const,

  // Auth
  currentUser: ['auth', 'me'] as const,

  // Admin
  systemConfig: ['admin', 'config'] as const,
  users: (filters?: SearchFilters) => ['admin', 'users', filters] as const,
  user: (id: string) => ['admin', 'users', id] as const,
  pipelineStatuses: ['admin', 'statuses'] as const,
  complianceChecklists: ['admin', 'compliance'] as const,
  auditLogs: (filters?: SearchFilters) => ['admin', 'audit', filters] as const,
  systemSettings: ['admin', 'settings'] as const,

  // Clients
  clients: (filters?: any) => ['clients', filters] as const,
  client: (id: string) => ['clients', id] as const,
  activeClientsList: ['clients', 'active', 'list'] as const,

  // Recruiter
  recruiterDashboard: ['recruiter', 'dashboard'] as const,
  requirements: (filters?: SearchFilters) => ['recruiter', 'requirements', filters] as const,
  requirement: (id: string) => ['recruiter', 'requirements', id] as const,
  candidates: (filters?: SearchFilters) => ['recruiter', 'candidates', filters] as const,
  candidate: (id: string) => ['recruiter', 'candidates', id] as const,
  submissions: (filters?: SearchFilters) => ['recruiter', 'submissions', filters] as const,
  submission: (id: string) => ['recruiter', 'submissions', id] as const,
  calls: (filters?: SearchFilters) => ['recruiter', 'calls', filters] as const,
  call: (id: string) => ['recruiter', 'calls', id] as const,
  candidatePipelineAnalytics: (filters?: SearchFilters) => ['recruiter', 'analytics', 'pipeline', filters] as const,
  recruiterPerformanceAnalytics: (filters?: SearchFilters) => ['recruiter', 'analytics', 'performance', filters] as const,

  // AI Analysis
  cvAnalysis: (id: string) => ['ai', 'analysis', 'cv', id] as const,
  jdAnalysis: (id: string) => ['ai', 'analysis', 'jd', id] as const,
  fitmentScore: (candidateId: string, requirementId: string) => ['ai', 'fitment', candidateId, requirementId] as const,
  interviewQuestions: (candidateId: string, requirementId: string) => ['ai', 'questions', candidateId, requirementId] as const,

  // Queries
  queries: (filters?: SearchFilters) => ['queries', filters] as const,
  query: (id: string) => ['queries', id] as const,

  // Email
  emailTemplates: ['email', 'templates'] as const,
  emailTracking: (id: string) => ['email', 'tracking', id] as const,
  emailAnalytics: (filters?: SearchFilters) => ['email', 'analytics', filters] as const,
  // Workforce
  workforceSummary: (days: number = 7) => ['workforce', 'summary', days] as const,
  workforceKPIs: (days: number = 7) => ['workforce', 'kpis', days] as const,
  workforceActions: (days: number = 7) => ['workforce', 'actions', days] as const,
  workforceInsights: (limit: number = 25, nextKey?: string) => ['workforce', 'insights', limit, nextKey] as const,
  workforceThread: (threadId: string, limit: number = 50, nextKey?: string) => ['workforce', 'thread', threadId, limit, nextKey] as const,
  workforceClients: (days: number = 7) => ['workforce', 'clients', days] as const,
  workforceAlerts: (days: number = 7) => ['workforce', 'alerts', days] as const,
  workforceDigest: (days: number = 1) => ['workforce', 'digest', days] as const,
};

// Health Check Hooks
export const useHealth = (options?: UseQueryOptions<ApiResponse<any>>) => {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => apiClient.getHealth(),
    ...options,
  });
};

export const useHealthReady = (options?: UseQueryOptions<ApiResponse<any>>) => {
  return useQuery({
    queryKey: queryKeys.healthReady,
    queryFn: () => apiClient.getHealthReady(),
    ...options,
  });
};

export const useHealthDetailed = (options?: UseQueryOptions<ApiResponse<any>>) => {
  return useQuery({
    queryKey: queryKeys.healthDetailed,
    queryFn: () => apiClient.getHealthDetailed(),
    ...options,
  });
};

export const useHealthMetrics = (options?: UseQueryOptions<ApiResponse<any>>) => {
  return useQuery({
    queryKey: queryKeys.healthMetrics,
    queryFn: () => apiClient.getHealthMetrics(),
    ...options,
  });
};

// Authentication Hooks
export const useCurrentUser = (options?: UseQueryOptions<User>) => {
  const [hasToken, setHasToken] = React.useState(() => apiClient.isAuthenticated());
  
  // Update state when authentication changes
  React.useEffect(() => {
    const checkAuth = () => {
      const authStatus = apiClient.isAuthenticated();
      if (authStatus !== hasToken) {
        console.log('🔍 useCurrentUser: auth status changed from', hasToken, 'to', authStatus);
        setHasToken(authStatus);
      }
    };
    
    // Check immediately
    checkAuth();
    
    // Listen for storage changes (when token is set in another tab/window)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        console.log('🔍 useCurrentUser: storage change detected for access_token');
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Set up interval to check for token changes
    const interval = setInterval(checkAuth, 1000); // Check every second for faster response
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [hasToken]); // Include hasToken dependency
  
  console.log('🔍 useCurrentUser: enabled =', hasToken, 'token present =', !!localStorage.getItem('access_token'));
  
  const query = useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: () => {
      console.log('🔄 useCurrentUser: Executing query function');
      return apiClient.getCurrentUser();
    },
    enabled: hasToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Only retry if it's a network error or 5xx error, not 401/403
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.log('❌ useCurrentUser: Auth error, not retrying');
        return false;
      }
      return failureCount < 2; // Retry up to 2 times for other errors
    },
    retryDelay: 1000, // Wait 1 second before retry
    // React Query will automatically deduplicate requests with the same key
    // This prevents multiple simultaneous calls to the same endpoint
    ...options,
  });

  console.log('🔍 useCurrentUser: Query result:', {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isSuccess: query.isSuccess,
    isError: query.isError
  });

  return query;
};

export const useLogin = (options?: UseMutationOptions<LoginResponse, Error, LoginRequest>) => {
  const isProcessingRef = React.useRef(false);
  
  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      apiDebug.query('LOGIN_MUTATION_START', { email: credentials.email });
      const response = await apiClient.login(credentials);
      apiDebug.query('LOGIN_MUTATION_SUCCESS', { hasTokens: !!response.access_token });
      return response;
    },
    onSuccess: async () => {
      // Prevent duplicate processing in React Strict Mode
      if (isProcessingRef.current) {
        return;
      }
      
      isProcessingRef.current = true;
      
      try {
        // Token storage is now handled in AuthContext
        // Let React Query automatically fetch user data by enabling the useCurrentUser hook
        // The hook will automatically run when apiClient.isAuthenticated() returns true
        console.log('🔄 Login success, tokens will be stored by AuthContext');
      } catch (error) {
        console.error('❌ Login success error:', error);
      } finally {
        // Reset the ref after a short delay to allow for React Strict Mode double-invocation
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 100);
      }
    },
    onError: (error) => {
      console.error('❌ Login mutation error:', error);
      // The error will be handled by AuthContext, but we can log the user-friendly message here
      const friendlyMessage = extractErrorMessage(error);
      console.error('❌ User-friendly error message:', friendlyMessage);
    },
    onMutate: () => {
      // Mutation starting
    },
    onSettled: () => {
      // Mutation settled
    },
    ...options,
  });
};

export const useLogout = (options?: UseMutationOptions<void, Error, void>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => apiClient.logout(),
    onSuccess: () => {
      console.log('🔄 useLogout: onSuccess callback executing');
      apiClient.clearAuthToken();
      queryClient.clear();
      console.log('✅ useLogout: Auth tokens cleared and query cache cleared');
    },
    onError: (error) => {
      console.error('❌ useLogout: Error during logout:', error);
      // Even on error, clear tokens and cache
      apiClient.clearAuthToken();
      queryClient.clear();
    },
    ...options,
  });
};

export const useRefreshToken = (options?: UseMutationOptions<LoginResponse, Error, RefreshRequest>) => {
  return useMutation({
    mutationFn: (refreshData: RefreshRequest) => apiClient.refreshToken(refreshData),
    ...options,
  });
};

// Admin Hooks
export const useSystemConfig = (options?: UseQueryOptions<ApiResponse<any>>) => {
  return useQuery({
    queryKey: queryKeys.systemConfig,
    queryFn: () => apiClient.getSystemConfig(),
    ...options,
  });
};

export const useUpdateSystemConfig = (options?: UseMutationOptions<ApiResponse<any>, Error, Record<string, any>>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (config: Record<string, any>) => apiClient.updateSystemConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.systemConfig });
    },
    ...options,
  });
};

export const useUsers = (filters?: SearchFilters, options?: UseQueryOptions<User[]>) => {
  return useQuery({
    queryKey: queryKeys.users(filters),
    queryFn: () => apiClient.getUsers(filters),
    ...options,
  });
};

export const useUsersPaginated = (filters?: SearchFilters & { page?: number; page_size?: number; search?: string; role?: string; org_id?: string; is_active?: boolean }, options?: UseQueryOptions<PaginatedUsersResponse<User>>) => {
  return useQuery({
    queryKey: queryKeys.users(filters),
    queryFn: () => apiClient.getUsersPaginated(filters),
    ...options,
  });
};

export const useCreateUser = (options?: UseMutationOptions<User, Error, UserCreate>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userData: UserCreate) => apiClient.createUser(userData),
    onSuccess: () => {
      // Invalidate all user-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', undefined] });
    },
    ...options,
  });
};

export const useUpdateUser = (options?: UseMutationOptions<User, Error, { id: string; data: UserUpdate }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdate }) => apiClient.updateUser(id, data),
    onSuccess: (data) => {
      // Update individual user cache
      queryClient.setQueryData(queryKeys.user(data.id), data);
      // Invalidate all user-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', undefined] });
    },
    ...options,
  });
};

export const useDeleteUser = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => apiClient.deleteUser(userId),
    onSuccess: () => {
      // Invalidate all user-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', undefined] });
    },
    ...options,
  });
};

export const useResendUserEmail = (options?: UseMutationOptions<{ message: string; email: string }, Error, string>) => {
  return useMutation({
    mutationFn: (userId: string) => apiClient.resendUserEmail(userId),
    ...options,
  });
};

export const useSubmitSupportTicket = (options?: UseMutationOptions<{ message: string }, Error, { subject: string; message: string; priority: string; category: string }>) => {
  return useMutation({
    mutationFn: (data: { subject: string; message: string; priority: string; category: string }) => apiClient.submitSupportTicket(data),
    ...options,
  });
};

export const useUpdateUserRole = (options?: UseMutationOptions<User, Error, { id: string; data: UserRoleUpdate }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserRoleUpdate }) => apiClient.updateUserRole(id, data),
    onSuccess: (data) => {
      // Update individual user cache
      queryClient.setQueryData(queryKeys.user(data.id), data);
      // Invalidate all user-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', undefined] });
    },
    ...options,
  });
};

export const useBulkCreateUsers = (options?: UseMutationOptions<{ created_count: number; error_count: number; created: any[]; errors: any[] }, Error, { file: File; sendEmails?: boolean }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ file, sendEmails = true }: { file: File; sendEmails?: boolean }) => apiClient.bulkCreateUsers(file, sendEmails),
    onSuccess: () => {
      // Invalidate all user-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', undefined] });
    },
    ...options,
  });
};

export const usePipelineStatuses = (options?: UseQueryOptions<PipelineStatus[]>) => {
  return useQuery({
    queryKey: queryKeys.pipelineStatuses,
    queryFn: () => apiClient.getPipelineStatuses(),
    ...options,
  });
};

export const useCreatePipelineStatus = (options?: UseMutationOptions<PipelineStatus, Error, Omit<PipelineStatus, 'id' | 'created_at' | 'updated_at'>>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (statusData: Omit<PipelineStatus, 'id' | 'created_at' | 'updated_at'>) => apiClient.createPipelineStatus(statusData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pipelineStatuses });
    },
    ...options,
  });
};

export const useUpdatePipelineStatus = (options?: UseMutationOptions<PipelineStatus, Error, { id: string; data: Partial<PipelineStatus> }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PipelineStatus> }) => apiClient.updatePipelineStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pipelineStatuses });
    },
    ...options,
  });
};

// Requirement Candidates hooks
export const useRequirementCandidates = (requirementId: string, page: number = 1, pageSize: number = 20) => {
  return useQuery({
    queryKey: ['requirementCandidates', requirementId, page, pageSize],
    queryFn: () => apiClient.getRequirementCandidates(requirementId, page, pageSize),
    enabled: !!requirementId,
  });
};

export const useRequirementsCandidateCounts = (requirementIds: string[]) => {
  return useQuery({
    queryKey: ['requirementsCandidateCounts', requirementIds],
    queryFn: () => apiClient.getRequirementsCandidateCounts(requirementIds),
    enabled: requirementIds.length > 0,
  });
};

// Requirements hooks
export const useRequirements = (filters?: {
  page?: number;
  page_size?: number;
  status_filter?: string;
  skills?: string;
  location?: string;
  seniority?: string;
  search?: string;
  client_filter?: string;
  sort_by?: string;
  sort_order?: string;
}, options?: UseQueryOptions<any, Error>) => {
  return useQuery({
    queryKey: ['requirements', filters],
    queryFn: () => apiClient.getRequirements(filters),
    ...options,
  });
};

export const useRequirement = (id: string, options?: UseQueryOptions<any, Error>) => {
  return useQuery({
    queryKey: ['requirement', id],
    queryFn: () => apiClient.getRequirement(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateRequirement = (options?: UseMutationOptions<any, Error, any>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiClient.createRequirement(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['requirements-all'] });
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      // Show success toast notification with longer duration
      toast.success(`Requirement "${variables?.title || 'New Requirement'}" created successfully!`, {
        duration: 5000,
        position: 'top-center',
      });
    },
    onError: (error) => {
      // Show error toast notification
      toast.error(`Failed to create requirement: ${error.message}`, {
        duration: 6000,
        position: 'top-center',
      });
    },
    ...options,
  });
};

export const useUpdateRequirement = (options?: UseMutationOptions<any, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiClient.updateRequirement(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['requirements-all'] });
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['requirement', variables.id] });
      // Show success toast notification with longer duration
      toast.success(`Requirement "${variables?.data?.title || 'Requirement'}" updated successfully!`, {
        duration: 5000,
        position: 'top-center',
      });
    },
    onError: (error) => {
      // Show error toast notification
      toast.error(`Failed to update requirement: ${error.message}`, {
        duration: 6000,
        position: 'top-center',
      });
    },
    ...options,
  });
};

export const useDeleteRequirement = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.deleteRequirement(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['requirements-all'] });
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.removeQueries({ queryKey: ['requirement', id] });
    },
    ...options,
  });
};

// Note: useParseJobDescription hook removed - use useJDProcessing hook instead

export const useDeletePipelineStatus = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (statusId: string) => apiClient.deletePipelineStatus(statusId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pipelineStatuses });
    },
    ...options,
  });
};

export const useComplianceChecklists = (options?: UseQueryOptions<ComplianceChecklist[]>) => {
  return useQuery({
    queryKey: queryKeys.complianceChecklists,
    queryFn: () => apiClient.getComplianceChecklists(),
    ...options,
  });
};

export const useCreateComplianceChecklist = (options?: UseMutationOptions<ComplianceChecklist, Error, Omit<ComplianceChecklist, 'id' | 'created_at' | 'updated_at'>>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (checklistData: Omit<ComplianceChecklist, 'id' | 'created_at' | 'updated_at'>) => apiClient.createComplianceChecklist(checklistData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceChecklists });
    },
    ...options,
  });
};

export const useUpdateComplianceChecklist = (options?: UseMutationOptions<ComplianceChecklist, Error, { id: string; data: Partial<ComplianceChecklist> }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ComplianceChecklist> }) => apiClient.updateComplianceChecklist(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceChecklists });
    },
    ...options,
  });
};

export const useDeleteComplianceChecklist = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (checklistId: string) => apiClient.deleteComplianceChecklist(checklistId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceChecklists });
    },
    ...options,
  });
};

export const useAuditLogs = (filters?: SearchFilters, options?: UseQueryOptions<PaginatedResponse<AuditLog>>) => {
  return useQuery({
    queryKey: queryKeys.auditLogs(filters),
    queryFn: () => apiClient.getAuditLogs(filters),
    ...options,
  });
};

export const useSystemSettings = (options?: UseQueryOptions<ApiResponse<any>>) => {
  return useQuery({
    queryKey: queryKeys.systemSettings,
    queryFn: () => apiClient.getSystemSettings(),
    ...options,
  });
};

export const useUpdateSystemSettings = (options?: UseMutationOptions<ApiResponse<any>, Error, Record<string, any>>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (settings: Record<string, any>) => apiClient.updateSystemSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.systemSettings });
    },
    ...options,
  });
};

// Recruiter Hooks
export const useRecruiterDashboard = (options?: UseQueryOptions<DashboardKPIs>) => {
  return useQuery({
    queryKey: queryKeys.recruiterDashboard,
    queryFn: () => apiClient.getRecruiterDashboard(),
    ...options,
  });
};



export const useCandidates = (filters?: SearchFilters, options?: UseQueryOptions<PaginatedResponse<Candidate>>) => {
  return useQuery({
    queryKey: queryKeys.candidates(filters),
    queryFn: () => apiClient.getCandidates(filters),
    ...options,
  });
};

export const useCandidate = (id: string, options?: UseQueryOptions<Candidate>) => {
  return useQuery({
    queryKey: queryKeys.candidate(id),
    queryFn: () => apiClient.getCandidates({ page: 1, page_size: 1, search: id }).then(response => response.items[0]),
    enabled: !!id,
    ...options,
  });
};

export const useCreateCandidate = (options?: UseMutationOptions<Candidate, Error, CandidateCreate>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (candidateData: CandidateCreate) => apiClient.createCandidate(candidateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.candidates() });
    },
    ...options,
  });
};

export const useUpdateCandidate = (options?: UseMutationOptions<Candidate, Error, { id: string; data: CandidateUpdate }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CandidateUpdate }) => apiClient.updateCandidate(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.candidate(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.candidates() });
    },
    ...options,
  });
};

export const useBulkUploadCandidates = (options?: UseMutationOptions<Candidate[], Error, CandidateCreate[]>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (candidates: CandidateCreate[]) => apiClient.bulkUploadCandidates(candidates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.candidates() });
    },
    ...options,
  });
};

// PDF Generation Hooks
export const useGenerateAnalysisPDF = (options?: UseMutationOptions<{ message: string; message_id: string; status: string }, Error, { candidateId: string; requirementId?: string; reportType?: 'client' | 'internal' }>) => {
  return useMutation({
    mutationFn: ({ candidateId, requirementId, reportType }) => 
      apiClient.generateAnalysisPDF(candidateId, { requirementId, reportType }),
    ...options,
  });
};

export const useAnalysisPDFStatus = (candidateId: string, messageId: string, options?: UseQueryOptions<{ status: string; message: string; pdf_url?: string }>) => {
  return useQuery({
    queryKey: ['analysis-pdf-status', candidateId, messageId],
    queryFn: () => apiClient.getAnalysisPDFStatus(candidateId, messageId),
    enabled: !!candidateId && !!messageId,
    refetchInterval: (query) => {
      // Stop polling when PDF is ready or failed
      const data = query.state.data;
      return data?.status === 'completed' || data?.status === 'failed' ? false : 2000;
    },
    ...options,
  });
};

export const useSubmissions = (filters?: SearchFilters, options?: UseQueryOptions<PaginatedResponse<Submission>>) => {
  return useQuery({
    queryKey: queryKeys.submissions(filters),
    queryFn: () => apiClient.getSubmissions(filters),
    ...options,
  });
};

export const useSubmission = (id: string, options?: UseQueryOptions<Submission>) => {
  return useQuery({
    queryKey: queryKeys.submission(id),
    queryFn: () => apiClient.getSubmissions({ page: 1, page_size: 1, search: id }).then(response => response.items[0]),
    enabled: !!id,
    ...options,
  });
};

export const useCreateSubmission = (options?: UseMutationOptions<Submission, Error, SubmissionCreate>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (submissionData: SubmissionCreate) => apiClient.submitCandidate(submissionData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions() });
    },
    ...options,
  });
};

export const useUpdateSubmissionStatus = (options?: UseMutationOptions<Submission, Error, { id: string; data: SubmissionUpdate }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SubmissionUpdate }) => apiClient.updateSubmissionStatus(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.submission(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions() });
    },
    ...options,
  });
};

export const useCalls = (filters?: SearchFilters, options?: UseQueryOptions<PaginatedResponse<Call>>) => {
  return useQuery({
    queryKey: queryKeys.calls(filters),
    queryFn: () => apiClient.getCalls(filters),
    ...options,
  });
};

// Removed useCall hook - getCallStatus returns different type than Call

export const useCreateCall = (options?: UseMutationOptions<Call, Error, CallCreate>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (callData: CallCreate) => apiClient.createCall(callData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calls() });
    },
    ...options,
  });
};

export const useUpdateCall = (options?: UseMutationOptions<Call, Error, { id: string; data: CallUpdate }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CallUpdate }) => apiClient.updateCallTranscript(id, data as CallTranscriptUpdate),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.call(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.calls() });
    },
    ...options,
  });
};

// Removed useStartCallRecording and useEndCallRecording - not available in new API

export const useCandidatePipelineAnalytics = (filters?: SearchFilters, options?: UseQueryOptions<CandidatePipelineAnalytics>) => {
  return useQuery({
    queryKey: queryKeys.candidatePipelineAnalytics(filters),
    queryFn: () => apiClient.getCandidatePipelineAnalytics(filters),
    ...options,
  });
};

export const useRecruiterPerformanceAnalytics = (filters?: SearchFilters, options?: UseQueryOptions<RecruiterPerformanceAnalytics[]>) => {
  return useQuery({
    queryKey: queryKeys.recruiterPerformanceAnalytics(filters),
    queryFn: () => apiClient.getRecruiterPerformanceAnalytics(filters),
    ...options,
  });
};

// AI Analysis Hooks
export const useAnalyzeCV = (options?: UseMutationOptions<CVAnalysis, Error, { file: File; requirementId?: string }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ file, requirementId }: { file: File; requirementId?: string }) => apiClient.analyzeCV(file, requirementId),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.cvAnalysis(data.id), data);
    },
    ...options,
  });
};

export const useAnalyzeJD = (options?: UseMutationOptions<JDAnalysis, Error, File>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (file: File) => apiClient.analyzeJD(file),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.jdAnalysis(data.id), data);
    },
    ...options,
  });
};

export const useCalculateFitmentScore = (options?: UseMutationOptions<FitmentScore, Error, { candidateId: string; requirementId: string }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ candidateId, requirementId }: { candidateId: string; requirementId: string }) => 
      apiClient.calculateFitmentScore(candidateId, requirementId),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.fitmentScore(data.candidate_id, data.requirement_id), data);
    },
    ...options,
  });
};

export const useGenerateInterviewQuestions = (options?: UseMutationOptions<InterviewQuestions, Error, { candidateId: string; requirementId: string }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ candidateId, requirementId }: { candidateId: string; requirementId: string }) => 
      apiClient.generateInterviewQuestions(candidateId, requirementId),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.interviewQuestions(data.candidate_id, data.requirement_id), data);
    },
    ...options,
  });
};

// Removed problematic hooks that had type mismatches with the new analysis API

// Query System Hooks
export const useQueries = (filters?: SearchFilters, options?: UseQueryOptions<PaginatedResponse<Query>>) => {
  return useQuery({
    queryKey: queryKeys.queries(filters),
    queryFn: () => apiClient.getQueries(filters),
    ...options,
  });
};

export const useQueryDetails = (id: string, options?: UseQueryOptions<Query>) => {
  return useQuery({
    queryKey: queryKeys.query(id),
    queryFn: () => apiClient.getQueryDetails(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateQuery = (options?: UseMutationOptions<Query, Error, QueryCreate>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (queryData: QueryCreate) => apiClient.generateQuery(queryData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.queries() });
    },
    ...options,
  });
};

export const useUpdateQuery = (options?: UseMutationOptions<Query, Error, { id: string; data: QueryUpdate }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: QueryUpdate }) => apiClient.updateQuery(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.query(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.queries() });
    },
    ...options,
  });
};

export const useDeleteQuery = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (queryId: string) => apiClient.deleteQuery(queryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.queries() });
    },
    ...options,
  });
};

export const useApproveQuery = (options?: UseMutationOptions<Query, Error, string>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (queryId: string) => apiClient.approveQuery(queryId),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.query(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.queries() });
    },
    ...options,
  });
};

export const useRejectQuery = (options?: UseMutationOptions<Query, Error, { id: string; reason?: string }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => apiClient.rejectQuery(id, reason),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.query(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.queries() });
    },
    ...options,
  });
};

export const useCrossMatchCandidates = (options?: UseMutationOptions<ApiResponse<any>, Error, { candidateIds: string[]; requirementIds: string[] }>) => {
  return useMutation({
    mutationFn: ({ candidateIds, requirementIds }: { candidateIds: string[]; requirementIds: string[] }) => 
      apiClient.crossMatchCandidates(candidateIds, requirementIds),
    ...options,
  });
};

// Email System Hooks
export const useEmailTemplates = (options?: UseQueryOptions<EmailTemplate[]>) => {
  return useQuery({
    queryKey: queryKeys.emailTemplates,
    queryFn: () => apiClient.getEmailTemplates(),
    ...options,
  });
};

export const useSendEmail = (options?: UseMutationOptions<ApiResponse<any>, Error, EmailSend>) => {
  return useMutation({
    mutationFn: (emailData: EmailSend) => apiClient.sendEmail(emailData),
    ...options,
  });
};

export const useCreateEmailDraft = (options?: UseMutationOptions<ApiResponse<any>, Error, EmailSend>) => {
  return useMutation({
    mutationFn: (emailData: EmailSend) => apiClient.createEmailDraft(emailData),
    ...options,
  });
};

export const useEmailTracking = (id: string, options?: UseQueryOptions<EmailTracking>) => {
  return useQuery({
    queryKey: queryKeys.emailTracking(id),
    queryFn: () => apiClient.getEmailTracking(id),
    enabled: !!id,
    ...options,
  });
};

export const useEmailAnalytics = (recruiterId?: string, days = 30, options?: UseQueryOptions<any>) => {
  return useQuery({
    queryKey: ['email-analytics', recruiterId, days],
    queryFn: () => apiClient.getEmailAnalytics(recruiterId, days),
    ...options,
  });
};

// Workforce Hooks
export const useWorkforceSummary = (days: number = 7, options?: UseQueryOptions<any>) => {
  return useQuery({
    queryKey: queryKeys.workforceSummary(days),
    queryFn: () => apiClient.getWorkforceSummary(days),
    ...options,
  });
};

export const useWorkforceKPIs = (days: number = 7, options?: UseQueryOptions<any>) => {
  return useQuery({
    queryKey: queryKeys.workforceKPIs(days),
    queryFn: () => apiClient.getWorkforceKPIs(days),
    ...options,
  });
};

export const useWorkforceActions = (days: number = 7, options?: UseQueryOptions<{ items: any[]; total: number }>) => {
  return useQuery({
    queryKey: queryKeys.workforceActions(days),
    queryFn: () => apiClient.getWorkforceActions(days),
    ...options,
  });
};

export const useWorkforceInsights = (limit: number = 25, nextKey?: string, options?: UseQueryOptions<{ items: any[]; next_key?: string | null; count: number }>) => {
  return useQuery({
    queryKey: queryKeys.workforceInsights(limit, nextKey),
    queryFn: () => apiClient.getWorkforceInsights(limit, nextKey),
    ...options,
  });
};

export const useWorkforceThread = (threadId: string, limit: number = 50, nextKey?: string, options?: UseQueryOptions<{ items: any[]; next_key?: string | null; count: number }>) => {
  return useQuery({
    queryKey: queryKeys.workforceThread(threadId, limit, nextKey),
    queryFn: () => apiClient.getWorkforceThread(threadId, limit, nextKey),
    enabled: !!threadId,
    ...options,
  });
};

export const useWorkforceClients = (days: number = 7, options?: UseQueryOptions<{ items: any[]; total: number; lookback_days: number }>) => {
  return useQuery({
    queryKey: queryKeys.workforceClients(days),
    queryFn: () => apiClient.getWorkforceClients(days),
    ...options,
  });
};

export const useWorkforceAlerts = (days: number = 7, options?: UseQueryOptions<{ items: any[]; total: number; lookback_days: number }>) => {
  return useQuery({
    queryKey: queryKeys.workforceAlerts(days),
    queryFn: () => apiClient.getWorkforceAlerts(days),
    ...options,
  });
};

export const useWorkforceDigest = (days: number = 1, options?: UseQueryOptions<any>) => {
  return useQuery({
    queryKey: queryKeys.workforceDigest(days),
    queryFn: () => apiClient.getWorkforceDigest(days),
    ...options,
  });
};

// Requirement Value Management Hooks
export const useGetRequirementValues = () => {
  return useQuery({
    queryKey: ['requirement-values'],
    queryFn: () => apiClient.getRequirementValues(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGetRequirementValuesByCategory = (category: string) => {
  return useQuery({
    queryKey: ['requirement-values', category],
    queryFn: () => apiClient.getRequirementValuesByCategory(category),
    enabled: !!category,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateRequirementValue = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { category: string; value: string; display_name: string; description?: string; sort_order?: number; metadata?: Record<string, any> }) =>
      apiClient.createRequirementValue(data.category, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirement-values'] });
    },
  });
};

export const useUpdateRequirementValue = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { category: string; value_id: string; value?: string; display_name?: string; description?: string; sort_order?: number; metadata?: Record<string, any> }) =>
      apiClient.updateRequirementValue(data.category, data.value_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirement-values'] });
    },
  });
};

export const useDeleteRequirementValue = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { category: string; value_id: string }) =>
      apiClient.deleteRequirementValue(data.category, data.value_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirement-values'] });
    },
  });
};

export const useBulkUpdateRequirementValues = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { category: string; value_orders: Array<{ value_id: string; sort_order: number }> }) =>
      apiClient.bulkUpdateRequirementValues(data.category, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirement-values'] });
    },
  });
};

export const useImportDefaultRequirementValues = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { category: string; overwrite_existing?: boolean }) =>
      apiClient.importDefaultRequirementValues(data.category, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirement-values'] });
    },
  });
};

// Client Hooks
export const useClients = (filters?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  return useQuery({
    queryKey: queryKeys.clients(filters),
    queryFn: () => apiClient.getClients(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useClient = (id: string, options?: UseQueryOptions<Client>) => {
  return useQuery({
    queryKey: queryKeys.client(id),
    queryFn: () => apiClient.getClient(id),
    enabled: !!id,
    ...options,
  });
};

export const useActiveClientsList = () => {
  return useQuery({
    queryKey: queryKeys.activeClientsList,
    queryFn: () => apiClient.getActiveClientsList(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: ClientCreate) => apiClient.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeClientsList });
    },
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClientUpdate }) => 
      apiClient.updateClient(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.client(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeClientsList });
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeClientsList });
    },
  });
};

export const useRestoreClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiClient.restoreClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeClientsList });
    },
  });
};
